/**
 * UsersManagement — Admin-only page: list all users (admin/agent/warehouse/customer)
 * with actions: create new (admin/warehouse), reset password, block/unblock, delete.
 *
 * Note: creating an "agent" user is still done from the Agents page (linked to anagrafica
 * agente); creating a "customer" user is still done from Customers (Crea credenziali).
 * This page focuses on: ADMIN + WAREHOUSE creation + cross-role moderation.
 */
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, KeyRound, Ban, Unlock, Trash2, Search, ShieldAlert, ShieldCheck, UserCog, Loader2, FileSignature } from "lucide-react";
import GdprAdminDialog from "../../components/GdprAdminDialog";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_META = {
  purchasing: { label: "Acquisti", color: "bg-rose-100 text-rose-800 border-rose-200" },
  production: { label: "Produzione", color: "bg-sky-100 text-sky-800 border-sky-200" },
  wms: { label: "iTRACE WMS", color: "bg-orange-100 text-orange-800 border-orange-200" },
  admin: { label: "Amministratore", color: "bg-rose-100 text-rose-700 border-rose-200" },
  warehouse: { label: "Magazzino", color: "bg-blue-100 text-blue-700 border-blue-200" },
  agent: { label: "Agente", color: "bg-amber-100 text-amber-700 border-amber-200" },
  customer: { label: "Cliente", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  superadmin: { label: "Super Admin", color: "bg-purple-100 text-purple-700 border-purple-200" },
};

function generateRandomPassword() {
  const charset = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789";
  let s = "";
  for (let i = 0; i < 10; i++) s += charset[Math.floor(Math.random() * charset.length)];
  return s;
}

export default function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [gdprOpen, setGdprOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [me, setMe] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [users, me] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/auth/me`),
      ]);
      setUsers(users.data || []);
      setMe(me.data || null);
    } catch (e) {
      toast.error("Errore nel caricamento utenti");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => roleFilter === "all" || u.role === roleFilter)
      .filter((u) => {
        if (statusFilter === "all") return true;
        const blocked = !!u.is_blocked;
        return statusFilter === "blocked" ? blocked : !blocked;
      })
      .filter((u) =>
        !q ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.name || "").toLowerCase().includes(q)
      )
      .sort((a, b) => {
        // self first, then admin role, then alpha by email
        if (me && a.id === me.id) return -1;
        if (me && b.id === me.id) return 1;
        const ra = a.role === "admin" ? 0 : a.role === "warehouse" ? 1 : a.role === "agent" ? 2 : 3;
        const rb = b.role === "admin" ? 0 : b.role === "warehouse" ? 1 : b.role === "agent" ? 2 : 3;
        if (ra !== rb) return ra - rb;
        return (a.email || "").localeCompare(b.email || "");
      });
  }, [users, search, roleFilter, statusFilter, me]);

  const toggleBlock = async (user) => {
    const next = !user.is_blocked;
    const verb = next ? "BLOCCARE" : "SBLOCCARE";
    if (!window.confirm(`${verb} l'account di ${user.email}?${next ? "\nL'utente non potrà più effettuare il login." : ""}`)) return;
    try {
      await axios.put(`${API}/admin/users/${user.id}/block`, { is_blocked: next });
      toast.success(next ? "Account bloccato" : "Account sbloccato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const toggleGdpr = async (user) => {
    const next = !user.gdpr_pending;
    try {
      await axios.put(`${API}/admin/users/${user.id}/gdpr`, { pending: next });
      toast.success(next ? "Firma GDPR richiesta al prossimo accesso" : "Richiesta firma GDPR annullata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const togglePurchases = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}/purchases-access`, { enabled: !user.purchases_enabled });
      toast.success(user.purchases_enabled ? "Accesso Acquisti revocato" : "Accesso Acquisti abilitato");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const toggleProductionManager = async (user) => {
    try {
      await axios.patch(`${API}/production/users/${user.id}`, { is_manager: !user.production_manager });
      toast.success(user.production_manager ? "Flag responsabile Produzione rimosso" : "Utente ora responsabile Produzione");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Eliminare DEFINITIVAMENTE l'account ${user.email}?\nL'azione è irreversibile.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${user.id}`);
      toast.success("Account eliminato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="space-y-4" data-testid="users-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <UserCog className="w-6 h-6 text-[#8A1C2A]" /> Utenti & Accessi
          </h2>
          <p className="text-sm text-stone-500 mt-1">
            Gestisci utenti e accessi. Abilita gli utenti interni al gruppo Acquisti.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setGdprOpen(true)} data-testid="gdpr-admin-open-btn">
            <FileSignature className="w-4 h-4 mr-2" />Consensi GDPR
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="user-create-btn">
            <Plus className="w-4 h-4 mr-2" />Crea nuovo utente
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input className="pl-10" placeholder="Cerca per email o nome..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="user-search" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="user-role-filter"><SelectValue placeholder="Ruolo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i ruoli</SelectItem>
            <SelectItem value="admin">Amministratori</SelectItem>
            <SelectItem value="warehouse">Magazzino</SelectItem>
            <SelectItem value="purchasing" data-testid="user-filter-purchasing">Acquisti</SelectItem>
            <SelectItem value="production" data-testid="user-filter-production">Produzione</SelectItem>
            <SelectItem value="agent">Agenti</SelectItem>
            <SelectItem value="customer">Clienti</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="user-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="active">Solo attivi</SelectItem>
            <SelectItem value="blocked">Solo bloccati</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { key: "admin", count: users.filter(u => u.role === "admin").length, label: "Admin" },
          { key: "warehouse", count: users.filter(u => u.role === "warehouse").length, label: "Magazzino" },
          { key: "agent", count: users.filter(u => u.role === "agent").length, label: "Agenti" },
          { key: "customer", count: users.filter(u => u.role === "customer").length, label: "Clienti" },
        ].map((s) => (
          <Card key={s.key} className="p-3">
            <p className="text-[10px] uppercase text-stone-500 font-semibold">{s.label}</p>
            <p className="text-xl font-bold text-stone-900">{s.count}</p>
          </Card>
        ))}
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-stone-400" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-stone-500">Nessun utente trovato</div>
          ) : (
            <div className="divide-y divide-stone-100">
              {filtered.map((u) => {
                const meta = ROLE_META[u.role] || { label: u.role, color: "bg-stone-100 text-stone-700" };
                const isSelf = me?.id === u.id;
                const blocked = !!u.is_blocked;
                return (
                  <div key={u.id} className={`p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 ${blocked ? "bg-rose-50/40" : ""}`} data-testid={`user-row-${u.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`${meta.color} border text-[10px] font-semibold uppercase`}>{meta.label}</Badge>
                        {isSelf && <Badge className="bg-stone-100 text-stone-700 border-stone-200 border text-[10px]">TU</Badge>}
                        {blocked && (
                          <Badge className="bg-rose-100 text-rose-700 border-rose-300 border text-[10px] uppercase">
                            <Ban className="w-3 h-3 mr-1 inline" /> Bloccato
                          </Badge>
                        )}
                        {u.gdpr_pending && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300 border text-[10px] uppercase" data-testid={`gdpr-pending-badge-${u.id}`}>
                            <FileSignature className="w-3 h-3 mr-1 inline" /> Firma GDPR al prossimo accesso
                          </Badge>
                        )}
                        {!u.gdpr_pending && u.gdpr_signed_at && (
                          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 border text-[10px] uppercase" data-testid={`gdpr-signed-badge-${u.id}`}>
                            <ShieldCheck className="w-3 h-3 mr-1 inline" /> GDPR firmato {u.gdpr_signed_at.slice(0, 10).split("-").reverse().join("/")}
                          </Badge>
                        )}
                      </div>
                      <p className="font-semibold text-stone-900 truncate mt-1">{u.name || "—"}</p>
                      <p className="text-xs text-stone-500 truncate">{u.email}</p>
                    </div>
                    <div className="flex flex-wrap gap-1 sm:gap-2">
                      {["agent", "warehouse"].includes(u.role) && <Button size="sm" variant="outline" onClick={() => togglePurchases(u)} className={u.purchases_enabled ? "text-emerald-700" : ""} data-testid={`user-purchases-access-${u.id}`}>{u.purchases_enabled ? "Acquisti abilitato · Revoca" : "Abilita Acquisti"}</Button>}
                      {u.role === "production" && <Button size="sm" variant="outline" onClick={() => toggleProductionManager(u)} className={u.production_manager ? "text-sky-700" : ""} data-testid={`user-production-manager-${u.id}`}>{u.production_manager ? "Responsabile Produzione · Revoca" : "Rendi responsabile Produzione"}</Button>}
                      {!isSelf && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleGdpr(u)}
                          className={u.gdpr_pending ? "text-stone-600" : "text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300"}
                          data-testid={`user-gdpr-${u.id}`}
                        >
                          <FileSignature className="w-3.5 h-3.5 mr-1" />
                          {u.gdpr_pending ? "Annulla firma GDPR" : "Richiedi firma GDPR"}
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setResetTarget(u)} data-testid={`user-reset-${u.id}`}>
                        <KeyRound className="w-3.5 h-3.5 mr-1" />Reset password
                      </Button>
                      {!isSelf && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleBlock(u)}
                          className={blocked ? "text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300" : "text-amber-700 hover:bg-amber-50 hover:border-amber-300"}
                          data-testid={`user-toggle-${u.id}`}
                        >
                          {blocked ? <><Unlock className="w-3.5 h-3.5 mr-1" />Sblocca</> : <><Ban className="w-3.5 h-3.5 mr-1" />Blocca</>}
                        </Button>
                      )}
                      {!isSelf && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteUser(u)}
                          className="text-rose-700 hover:bg-rose-50 hover:border-rose-300"
                          data-testid={`user-delete-${u.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" />Elimina
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Helper info */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-3 text-xs text-amber-800 flex items-start gap-2">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Come si creano gli altri tipi di account?</p>
            <p>Da questa pagina puoi creare account <strong>Admin</strong>, <strong>Magazzino</strong> e <strong>Acquisti</strong>. Gli agenti e i magazzinieri possono essere abilitati anche ad Acquisti mantenendo il proprio accesso. Per creare un account Agente usa Anagrafiche → Agenti; per un Cliente usa Anagrafiche → Clienti.</p>
          </div>
        </CardContent>
      </Card>

      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onSaved={load} />
      <ResetPasswordDialog target={resetTarget} onClose={() => setResetTarget(null)} />
      <GdprAdminDialog open={gdprOpen} onClose={() => setGdprOpen(false)} />
    </div>
  );
}

// ===================== CreateUserDialog =====================

function CreateUserDialog({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", role: "admin", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => { if (open) { setForm({ email: "", name: "", role: "admin", password: generateRandomPassword() }); setShowPwd(false); } }, [open]);

  const submit = async () => {
    if (!form.email.trim() || !form.name.trim() || (form.password || "").length < 6) {
      toast.error("Compila email, nome e password (min 6 caratteri)");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/users`, {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        role: form.role,
        password: form.password,
      });
      toast.success(`Account ${form.role} creato`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="user-create-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#8A1C2A]" />Crea nuovo utente</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">Ruolo *</Label>
            <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="user-create-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Amministratore (accesso completo)</SelectItem>
                <SelectItem value="warehouse">Magazzino (gestione ordini in evasione)</SelectItem>
                <SelectItem value="purchasing" data-testid="user-create-role-purchasing">Acquisti (fornitori e ordini condivisi)</SelectItem>
                <SelectItem value="production" data-testid="user-create-role-production">Produzione (rese, costi e lavorazioni · accesso da /produzione)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Nome completo *</Label>
            <Input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Mario Rossi" data-testid="user-create-name" />
          </div>
          <div>
            <Label className="text-sm">Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} placeholder="mario@giotto.it" data-testid="user-create-email" />
          </div>
          <div>
            <Label className="text-sm">Password *</Label>
            <div className="flex gap-2">
              <Input
                type={showPwd ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                className="font-mono"
                data-testid="user-create-password"
              />
              <Button type="button" variant="outline" size="sm" onClick={() => setShowPwd(!showPwd)}>{showPwd ? "Nascondi" : "Mostra"}</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setForm(f => ({ ...f, password: generateRandomPassword() }))} data-testid="user-create-genpwd">Genera</Button>
            </div>
            <p className="text-[11px] text-stone-500 mt-1">Comunica la password all&apos;utente. Potrà cambiarla in seguito.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#8A1C2A] hover:bg-[#6e1622]" data-testid="user-create-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
            Crea account
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===================== ResetPasswordDialog =====================

function ResetPasswordDialog({ target, onClose }) {
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (target) { setPwd(generateRandomPassword()); setShow(true); setSubmitting(false); } }, [target]);

  const submit = async () => {
    if ((pwd || "").length < 6) { toast.error("Password troppo corta (min 6)"); return; }
    setSubmitting(true);
    try {
      await axios.put(`${API}/admin/users/${target.id}/reset-password`, { new_password: pwd });
      toast.success(`Password resettata per ${target.email}`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="user-reset-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-600" />Reset password</DialogTitle>
        </DialogHeader>
        {target && (
          <div className="space-y-3">
            <div className="bg-stone-50 border border-stone-200 rounded p-3 text-sm">
              <p><span className="text-stone-500">Utente:</span> <strong>{target.email}</strong></p>
              <p><span className="text-stone-500">Ruolo:</span> {ROLE_META[target.role]?.label || target.role}</p>
            </div>
            <div>
              <Label className="text-sm">Nuova password * (min 6 caratteri)</Label>
              <div className="flex gap-2">
                <Input
                  type={show ? "text" : "password"}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="font-mono"
                  data-testid="user-reset-password"
                />
                <Button variant="outline" size="sm" onClick={() => setShow(!show)}>{show ? "Nascondi" : "Mostra"}</Button>
                <Button variant="outline" size="sm" onClick={() => setPwd(generateRandomPassword())} data-testid="user-reset-genpwd">Genera</Button>
              </div>
              <p className="text-[11px] text-stone-500 mt-1">Comunica la nuova password all&apos;utente via canale sicuro.</p>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="user-reset-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />}
            Reset password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
